/**
 * Hook para carregar atalhos do usuário (merge defaults + personalizações) da API
 *
 * Ao abrir o Tools, faz GET /users/me/shortcuts para obter atalhos.
 * O backend retorna o merge automático de defaults + personalizações do usuário.
 * - Sem fallback para localStorage
 * - Notifica em caso de erro
 * - ✅ OTIMIZAÇÃO: Suporta retry em caso de falha
 *
 * @example
 * const { shortcuts, isLoading, error, isLoaded, reload } = useShortcutsLoader();
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { sendToTerminal, logger } from "../lib/terminalLogger";
import { LogCategory } from "../config/logging";
import { TokenService } from "../lib/tokenService";

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════════════

import type { KeyCombo } from "../contexts/KeyboardContext";

/** Formato usado pelo KeyboardContext: { [toolId]: { [actionId]: KeyCombo } } */
export type ShortcutsState = Record<string, Record<string, KeyCombo>>;

export interface UseShortcutsLoaderReturn {
	/** Atalhos carregados no formato do KeyboardContext */
	shortcuts: ShortcutsState;
	/** Indica se está carregando */
	isLoading: boolean;
	/** Erro ocorrido (null se sucesso) */
	error: string | null;
	/** Indica se carregou com sucesso pelo menos uma vez */
	isLoaded: boolean;
	/** Função para carregar/recarregar atalhos */
	load: () => Promise<ShortcutsState | null>;
	/** Função para limpar erro */
	clearError: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useShortcutsLoader(): UseShortcutsLoaderReturn {
	const [shortcuts, setShortcuts] = useState<ShortcutsState>({});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isLoaded, setIsLoaded] = useState(false);

	// Evitar múltiplas chamadas simultâneas
	const loadingRef = useRef(false);
	// Contador de tentativas para retry
	const retryCountRef = useRef(0);
	const maxRetries = 2;

	const load = useCallback(async (): Promise<ShortcutsState | null> => {
		// Evitar chamadas duplicadas
		if (loadingRef.current) {
			logger.debug("[SHORTCUTS_LOADER] Load chamado mas já está carregando");
			return null;
		}

		// Verificar se há token antes de tentar carregar
		const token = TokenService.getAccessToken();
		const tokenPrefix = token ? token.substring(0, 20) + "..." : "null";

		if (!token) {
			logger.warn("[SHORTCUTS_LOADER] Load chamado sem token disponível");
			return null;
		}

		logger.info(
			`[SHORTCUTS_LOADER] Iniciando carregamento de atalhos (token: ${tokenPrefix})`,
		);

		loadingRef.current = true;
		setIsLoading(true);
		setError(null);

		try {
			// ✅ ATUALIZADO: Usar GET /users/me/shortcuts (retorna merge de defaults + personalizações)
			const response = await api("/users/me/shortcuts", {
				method: "GET",
			});

			if (
				response?.success &&
				response.data &&
				typeof response.data === "object"
			) {
				// O backend já retorna no formato correto: { [toolId]: { [actionId]: KeyCombo } }
				const state = response.data as ShortcutsState;

				const toolsCount = Object.keys(state).length;
				const bindingCount = Object.values(state).reduce(
					(sum, actions) => sum + Object.keys(actions).length,
					0,
				);

				// ✅ SANITIZAÇÃO: Remover atalhos inválidos (ex: "Num") que causam erro no registro
				const sanitizedState: ShortcutsState = {};
				const ignoredShortcuts: string[] = [];

				Object.entries(state).forEach(([toolId, actions]) => {
					if (!actions) return;
					sanitizedState[toolId] = {};

					Object.entries(actions).forEach(([actionId, keys]) => {
						// Verificar se alguma tecla é inválida (ex: "Num")
						const hasInvalidKey =
							Array.isArray(keys) &&
							keys.some((k) => k === "Num" || k === "Unidentified");

						if (hasInvalidKey) {
							ignoredShortcuts.push(
								`${toolId}:${actionId} (${keys.join("+")})`,
							);
						} else {
							sanitizedState[toolId][actionId] = keys;
						}
					});
				});

				if (ignoredShortcuts.length > 0) {
					logger.warn(
						`[SHORTCUTS_LOADER] Ignorando ${ignoredShortcuts.length} atalhos inválidos/legado`,
						{
							ignored: ignoredShortcuts,
						},
					);
				}

				setShortcuts(sanitizedState);
				setIsLoaded(true);
				retryCountRef.current = 0; // Reset retry counter on success

				logger.info(
					`[SHORTCUTS_LOADER] ${bindingCount} atalhos carregados com sucesso (merge defaults + personalizações)`,
				);

				return state;
			} else {
				throw new Error("Resposta inválida da API de atalhos");
			}
		} catch (err: any) {
			const message = err?.message || "Erro ao carregar atalhos do usuário";
			const status = err?.status || err?.response?.status;
			const errorName = err?.name || "UnknownError";
			const errorStack = err?.stack?.substring(0, 500) || "sem stack";
			const responseData = err?.response?.data;
			const errorCode = err?.code || "NO_CODE";

			// ✅ OTIMIZAÇÃO: Retry automático para erros 401/403 (token pode não estar pronto)
			if (
				(status === 401 || status === 403) &&
				retryCountRef.current < maxRetries
			) {
				retryCountRef.current++;

				logger.warn(
					`[SHORTCUTS_LOADER] Retry ${retryCountRef.current}/${maxRetries} após erro ${status}`,
				);

				// Aguardar um pouco antes de tentar novamente
				loadingRef.current = false;
				await new Promise((resolve) => setTimeout(resolve, 500));
				return load(); // Retry recursivo
			}

			setError(message);
			setShortcuts({}); // Sem fallback - fica vazio em caso de erro

			// ✅ DEBUG: Log detalhado do erro completo
			sendToTerminal(
				"❌ [SHORTCUTS_LOADER] Erro ao carregar atalhos - DETALHES",
				{
					errorMessage: message,
					errorStatus: status,
					errorName,
					errorCode,
					errorStack,
					responseData:
						typeof responseData === "object"
							? JSON.stringify(responseData).substring(0, 500)
							: responseData,
					retriesAttempted: retryCountRef.current,
					maxRetries,
					timestamp: new Date().toISOString(),
				},
				LogCategory.ERROR,
			);

			logger.error("[SHORTCUTS_LOADER] Falha ao carregar atalhos:", {
				message,
				status,
				errorName,
				errorCode,
				retries: retryCountRef.current,
			});

			return null;
		} finally {
			setIsLoading(false);
			loadingRef.current = false;
		}
	}, []);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	// ✅ OTIMIZAÇÃO: Auto-load quando detectar token disponível via evento auth:updated
	useEffect(() => {
		const handleAuthUpdated = () => {
			// Se já carregou, não recarregar
			if (isLoaded) return;

			// Verificar se há token
			const token = TokenService.getAccessToken();
			if (token && !loadingRef.current) {
				// Aguardar um pouco para garantir que o token está propagado
				setTimeout(() => {
					load();
				}, 100);
			}
		};

		window.addEventListener("auth:updated", handleAuthUpdated);
		return () => window.removeEventListener("auth:updated", handleAuthUpdated);
	}, [isLoaded, load]);

	return {
		shortcuts,
		isLoading,
		error,
		isLoaded,
		load,
		clearError,
	};
}
